import React from 'react'

import { ValueSetterParams } from 'ag-grid-community'
import { toast } from 'react-toastify'

import { useLazyGetApplicationQuery } from '@/graphql/services/application'
import { useUpdateApplicationMutation, useUpdatePropertyMutation } from '@/services/application'
import { useLazyGetApplicationCalcsQuery } from '@/services/applicationCalcs'
import { useUpdateTitleReportMutation } from '@/services/titleReport'
import { useCreateValuationMutation, useUpdateValuationMutation } from '@/services/valuation'

export function useWQValueSetters() {
  const [getApplication] = useLazyGetApplicationQuery()
  const [getApplicationCalcs] = useLazyGetApplicationCalcsQuery()
  const [updateApplication] = useUpdateApplicationMutation()
  const [updateTitleReport] = useUpdateTitleReportMutation()
  const [updateValuation] = useUpdateValuationMutation()
  const [createValuation] = useCreateValuationMutation()
  const [updateProperty] = useUpdatePropertyMutation()

  /** Updates the Application Model */
  const applicationValueSetter = (params: ValueSetterParams) => {
    const { field } = params.colDef
    updateApplication({
      external_id: params.data.external_id,
      [field]: params.newValue,
    }).then((resp) => {
      const fieldLabel = params.colDef.headerName
      if (resp['error']) {
        toast.error(`Error during ${fieldLabel} field update`, {
          containerId: 'global',
          position: 'top-center',
        })
      } else {
        params.node.setData({ ...params.data, [field]: params.newValue })
        toast.success(`${fieldLabel} field updated!`, {
          containerId: 'global',
          position: 'top-center',
        })
        return true // cell updated correctly
      }
    })
    return false // if cell didn't update correctly
  }
  /** Updates the Title Report Model */
  const titleReportValueSetter = (params: ValueSetterParams) => {
    const { field } = params.colDef
    const modelAttribute = field.endsWith('received') ? 'received' : 'ordered'
    getApplication({ externalId: params.data.external_id })
      .then((resp) => resp.data.titleReport)
      .then((title_report) =>
        updateTitleReport({
          uuid: title_report.uuid,
          [modelAttribute]: params.newValue,
        })
      )
      .then(() => {
        params.node.setData({ ...params.data, [params.colDef.field]: params.newValue })
        return true // if cell updated correctly
      })
    return false
  }

  /** Updates the Valuation Model */
  const valuationValueSetter = (params: ValueSetterParams) => {
    const { field } = params.colDef
    let modelAttribute
    if (field.endsWith('received')) {
      modelAttribute = 'received'
    } else if (field.endsWith('ordered')) {
      modelAttribute = 'ordered'
    } else if (field.endsWith('val_express')) {
      modelAttribute = 'val_express_override'
    } else {
      modelAttribute = field
    }
    getApplication({ externalId: params.data.external_id })
      .then((resp) => {
        const { valuations } = resp.data
        if (field === 'val_express') {
          // We need appraisal report for val express override
          const iValuenetOrHybrid = valuations.find(
            (v) => v.type === 'ivaluenet-55' || v.type === 'hybrid'
          )
          return iValuenetOrHybrid ?? null
        } else {
          // Return the last updated valuation
          return valuations.at(-1)
        }
      })
      .then((lastUpdatedValuation) => {
        return updateValuation({
          uuid: lastUpdatedValuation?.uuid ?? null,
          [modelAttribute]: params.newValue,
        })
      })
      .then(() => {
        if (field.endsWith('status')) {
          const options = {
            approved: 'Approved',
            'in-process': 'In Process',
            'issue-found': 'Issue Found',
            'recon-status': 'Recon Status',
          }
          params.node.setData({ ...params.data, [params.colDef.field]: options[params.newValue] })
        } else {
          params.node.setData({ ...params.data, [params.colDef.field]: params.newValue })
        }
        return true // if cell updated correctly
      })
    return false
  }

  const appraisalReportSetter = (params: ValueSetterParams) => {
    const { field } = params.colDef
    let modelAttribute
    if (field.endsWith('received')) {
      modelAttribute = 'received'
    } else if (field.endsWith('ordered')) {
      modelAttribute = 'ordered'
    } else {
      modelAttribute = field
    }

    getApplication(params.data.external_id)
      .then((resp) => {
        const { uuid, id } = resp.data
        return { uuid, id }
      })
      .then(({ uuid, id }) => {
        getApplicationCalcs(uuid)
          .then((resp) => {
            const { appraisal_report } = resp.data
            return appraisal_report
          })
          .then((appraisalReport) => {
            if (appraisalReport) {
              return updateValuation({
                uuid: appraisalReport?.uuid ?? null,
                [modelAttribute]: params.newValue,
              })
            } else {
              return createValuation({
                application: id,
                [modelAttribute]: params.newValue,
              })
            }
          })
          .then(() => {
            if (field.endsWith('status')) {
              const options = {
                approved: 'Approved',
                'in-process': 'In Process',
                'issue-found': 'Issue Found',
                'recon-status': 'Recon Status',
              }
              params.node.setData({
                ...params.data,
                [params.colDef.field]: options[params.newValue],
              })
            } else {
              params.node.setData({ ...params.data, [params.colDef.field]: params.newValue })
            }
            return true // if cell updated correctly
          })
      })
    return false
  }

  /** Updates the Property Model */
  const propertyValueSetter = (params: ValueSetterParams) => {
    const { field } = params.colDef
    updateProperty({
      uuid: params.data.property.uuid,
      [field]: params.newValue,
    }).then(() => {
      params.node.setData({
        ...params.data,
        property: { ...params.data.property, [field]: params.newValue },
      })
      return true // if cell updated correctly
    })
    return false
  }

  return {
    applicationValueSetter,
    titleReportValueSetter,
    valuationValueSetter,
    appraisalReportSetter,
    propertyValueSetter,
  }
}
